"use client";

import { useCallback, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Button, Input, TaiZenLogo } from "@/app/components/ui";
import { useToast } from "@/app/hooks";
import { safeLocalStorage } from "@/app/lib/storage";
import { setAccessToken, removeAccessToken } from "@/app/lib/cookie";
import { METHOD_TYPE, MIDDLEWARE } from "@/app/lib/enums";
import { api_url } from "@/app/api";
import { InputNotice } from "./InputNotice";
import { LoginWithSocial } from "./LoginWithSocial";
import { useAuth, useTenant } from "@/app/hooks";

export function LoginForm() {
   const translation = useTranslations("Auth.login");
   const router = useRouter();
   const pathname = usePathname();
   const searchParams = useSearchParams();
   const callbackUrl = searchParams.get(MIDDLEWARE.CALLBACK_URL);
   const [email, setEmail] = useState("");
   const [password, setPassword] = useState("");
   const { toast } = useToast();
   const [isAuthenticating, setIsAuthenticating] = useState(false);
   const { executeRecaptcha } = useGoogleReCaptcha();
   const tenant = useTenant();
   const { isSharedLoginAuthenticating } = useAuth();

   const handleLogin = useCallback(
      async (emailValue: string, passwordValue: string, recaptchaToken: string) => {
         if (isAuthenticating) return;
         setIsAuthenticating(true);
         try {
            const response = await fetch(api_url.login, {
               method: METHOD_TYPE.POST,
               headers: {
                  "Content-Type": "application/json",
                  ...(tenant ? { "Tenant-Id": tenant.tenantId.toString() } : {})
               },
               body: JSON.stringify({
                  email: emailValue,
                  password: passwordValue,
                  recaptchaToken,
                  recaptchaAction: "login"
               })
            });

            const data = await response.json();

            if (!response.ok) {
               throw new Error(
                  data.error || translation("error", { error: JSON.stringify(data) })
               );
            }
            const { token, user } = data;

            setAccessToken(token);

            safeLocalStorage.setItem(
               "user",
               JSON.stringify({
                  id: user.id,
                  email: user.email,
                  userName: user.username,
                  avatarUrl: user.avatarUrl
               })
            );

            await new Promise((resolve) => setTimeout(resolve, 500));
            toast({
               variant: "success",
               title: translation("success"),
               description: translation("success"),
               duration: 2_000,
               position: "bottom-center"
            });
            const locale = pathname?.split("/")[1] || "vi";
            if (callbackUrl) {
               window.location.href = callbackUrl;
            } else {
               router.replace(`/${locale}`);
               setTimeout(() => {
                  window.location.reload();
               }, 200);
            }
         } catch (error: any) {
            console.error("[Login] Error during auth:", error);
            safeLocalStorage.clear();
            removeAccessToken();
            toast({
               variant: "destructive",
               title: translation("error", { error: "" }),
               description: error.message || "Failed to get user info",
               duration: 2_000,
               position: "bottom-center"
            });
            setIsAuthenticating(false);
         }
      },
      [callbackUrl, isAuthenticating, pathname, router, toast, translation, tenant]
   );

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (isAuthenticating) return;

      try {
         if (!executeRecaptcha) {
            toast({
               variant: "destructive",
               title: translation("error", { error: "" }),
               description: "reCAPTCHA is not ready. Please try again."
            });
            return;
         }
         const token = await executeRecaptcha("login");
         if (!token) {
            toast({
               variant: "destructive",
               title: translation("error", { error: "" }),
               description: "Unable to get reCAPTCHA token. Please retry."
            });
            return;
         }
         return await handleLogin(email, password, token);
      } catch (error: any) {
         console.error("[Login] Form submission error:", error);
         toast({
            variant: "destructive",
            title: translation("error", { error: "" }),
            description:
               error instanceof Error
                  ? error.message
                  : translation("error", { error: JSON.stringify(error) }),
            duration: 2_000,
            position: "bottom-center"
         });
         setIsAuthenticating(false);
      }
   };
   const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

   return (
      <>
         {(isAuthenticating || isSharedLoginAuthenticating) && (
            <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center">
               <div className="flex flex-col items-center gap-4">
                  <div className="h-16 w-16 zen-spinner"></div>
                  <p className="text-muted-foreground text-sm animate-pulse">
                     {isSharedLoginAuthenticating
                        ? translation("sharedAuthenticating")
                        : translation("authenticating", {
                             fallback: "Authenticating..."
                          })}
                  </p>
               </div>
            </div>
         )}
         <main className="flex min-h-screen flex-col items-center justify-center px-4 sm:px-0">
            <div className="mx-auto flex w-full max-w-[400px] flex-col justify-center space-y-6 sm:max-w-[350px] sm:w-[350px]">
               <div className="flex flex-col space-y-2 text-center">
                  <TaiZenLogo title={translation("title")} />
               </div>

               <div className="grid gap-6">
                  <form onSubmit={handleSubmit}>
                     <div className="grid gap-4 w-full">
                        <div className="grid gap-2">
                           <label
                              htmlFor="email"
                              className="text-sm font-medium leading-none"
                           >
                              {translation("emailLabel")}
                           </label>
                           <Input
                              id="email"
                              autoComplete="email"
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              size="lg"
                              disabled={isAuthenticating}
                           />
                        </div>

                        <div className="grid gap-2">
                           <label
                              htmlFor="password"
                              className="text-sm font-medium leading-none"
                           >
                              {translation("passwordLabel")}
                           </label>
                           <Input
                              id="password"
                              type="password"
                              autoComplete="current-password"
                              required
                              size="lg"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              disabled={isAuthenticating}
                           />
                        </div>
                        <label htmlFor="input-notice" />
                        <Button
                           type="submit"
                           className="w-full"
                           size="lg"
                           disabled={isAuthenticating || !executeRecaptcha}
                        >
                           {translation("submitButton")}
                        </Button>

                        {googleClientId && (
                           <div className="relative">
                              <div className="absolute inset-0 flex items-center">
                                 <span className="w-full border-t" />
                              </div>
                              <div className="relative flex justify-center text-xs uppercase">
                                 <span className="bg-background px-2 text-muted-foreground">
                                    {translation("orText")}
                                 </span>
                              </div>
                           </div>
                        )}
                        {googleClientId && (
                           <LoginWithSocial onAuthStateChange={setIsAuthenticating} />
                        )}

                        <InputNotice />
                     </div>
                  </form>
               </div>
            </div>
         </main>
      </>
   );
}
